import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { ChevronIcon } from '@icons/ChevronIcon';
import { cn } from '@/lib/utils';

interface CarouselImage {
  url: string;
  alt?: string;
}

interface ImageCarouselProps {
  images: CarouselImage[];
  placeholder?: ReactNode;
  className?: string;
}

export function ImageCarousel({ images, placeholder, className }: ImageCarouselProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const count = images.length;

  // Reset index if images change and current index is out of bounds
  useEffect(() => {
    if (currentIndex >= count) {
      setCurrentIndex(Math.max(0, count - 1));
    }
  }, [count, currentIndex]);

  const goToPrevious = useCallback(() => {
    setCurrentIndex((i) => Math.max(0, i - 1));
  }, []);

  const goToNext = useCallback(() => {
    setCurrentIndex((i) => Math.min(count - 1, i + 1));
  }, [count]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        goToPrevious();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        goToNext();
      }
    },
    [goToPrevious, goToNext],
  );

  // Empty state
  if (count === 0) {
    return (
      <div className={cn('flex aspect-square items-center justify-center rounded-input bg-dinnerplate-white', className)}>
        {placeholder}
      </div>
    );
  }

  // Single image — no controls
  if (count === 1) {
    return (
      <div className={cn('flex aspect-square items-center justify-center rounded-input bg-dinnerplate-white overflow-hidden', className)}>
        <img src={images[0].url} alt={images[0].alt ?? ''} className="h-full w-full object-contain" />
      </div>
    );
  }

  // Multiple images — arrows + dots
  return (
    <div
      className={cn('relative flex aspect-square items-center justify-center rounded-input bg-dinnerplate-white overflow-hidden', className)}
      tabIndex={0}
      onKeyDown={handleKeyDown}
      role="region"
      aria-label="Image carousel"
      aria-roledescription="carousel"
    >
      <img
        src={images[currentIndex].url}
        alt={images[currentIndex].alt ?? ''}
        className="h-full w-full object-contain"
      />

      {/* Left arrow */}
      {currentIndex > 0 && (
        <button
          type="button"
          onClick={goToPrevious}
          aria-label="Previous image"
          className="absolute left-3 top-1/2 -translate-y-1/2 flex h-12 w-12 items-center justify-center rounded-full bg-white/80 text-off-black shadow-soft hover:bg-white"
        >
          <ChevronIcon direction="left" className="h-8 w-8" />
        </button>
      )}

      {/* Right arrow */}
      {currentIndex < count - 1 && (
        <button
          type="button"
          onClick={goToNext}
          aria-label="Next image"
          className="absolute right-3 top-1/2 -translate-y-1/2 flex h-12 w-12 items-center justify-center rounded-full bg-white/80 text-off-black shadow-soft hover:bg-white"
        >
          <ChevronIcon direction="right" className="h-8 w-8" />
        </button>
      )}

      {/* Dot indicators */}
      <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
        {images.map((_, i) => (
          <button
            key={i}
            type="button"
            onClick={() => setCurrentIndex(i)}
            aria-label={`Go to image ${i + 1}`}
            className={cn(
              'h-2 w-2 rounded-full transition-colors',
              i === currentIndex ? 'bg-off-black' : 'bg-off-black/30',
            )}
          />
        ))}
      </div>
    </div>
  );
}
